// src/components/BookingNotificationToasts.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isApprovalFirstEvent } from "../utils/eventBookingFlow";
import { approveEventBooking } from "../utils/approveEventBooking";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { auth, db as defaultDb } from "../firebase";
import { businessAdminPath, getBusinessRouteParam } from "../utils/businessSlug";
import { getCurrentEffectiveSession, isEffectiveSessionLoggedIn } from "../auth/effectiveSession";
import { makeUserScopedStorageKey } from "../auth/authStorage";

const TOAST_STORAGE_KEY = "loop_booking_toast_seen_v1";
const INITIAL_RECENT_MS = 90 * 1000;
const MAX_TOASTS = 3;

const DEVICE_STORAGE_KEY = "loop_notification_device_id_v1";

const DEFAULT_DEVICE_NOTIFICATION_TYPES = {
  bookings: true,
  reminders: true,
  changes: true,
  reviews: true,
  payments: true,
  inventory: true,
};

function getStoredNotificationDeviceId() {
  try {
    return localStorage.getItem(DEVICE_STORAGE_KEY) || "";
  } catch (_) {
    return "";
  }
}

function mergeToastDeviceSettings(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const scopes = Array.isArray(source.businessScopes)
    ? source.businessScopes.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const types = source.notificationTypes && typeof source.notificationTypes === "object"
    ? source.notificationTypes
    : {};
  const scopeMode = ["all", "active", "custom"].includes(source.scopeMode)
    ? source.scopeMode
    : (source.notifyAllBusinesses === false ? (scopes.length ? "custom" : "active") : "all");

  return {
    scopeMode,
    notifyAllBusinesses: source.notifyAllBusinesses === true || scopeMode === "all",
    businessScopes: scopes,
    notificationTypes: {
      ...DEFAULT_DEVICE_NOTIFICATION_TYPES,
      ...Object.fromEntries(
        Object.keys(DEFAULT_DEVICE_NOTIFICATION_TYPES).map((key) => [
          key,
          typeof types[key] === "boolean" ? types[key] : DEFAULT_DEVICE_NOTIFICATION_TYPES[key],
        ])
      ),
    },
    muteManualCreatedByMe: source.muteManualCreatedByMe !== false,
    loaded: source.loaded === true,
  };
}

function deviceAllowsBusinessToast(deviceSettings = {}, businessId = "") {
  const cleanBusinessId = String(businessId || "").trim();
  if (!cleanBusinessId) return true;

  const settings = mergeToastDeviceSettings(deviceSettings);
  if (settings.scopeMode === "all" || settings.notifyAllBusinesses === true) return true;

  const scopes = Array.isArray(settings.businessScopes) ? settings.businessScopes.map(String) : [];
  return scopes.includes(cleanBusinessId);
}

function deviceAllowsBookingToast(deviceSettings = {}) {
  const settings = mergeToastDeviceSettings(deviceSettings);
  return settings.notificationTypes?.bookings !== false;
}

function isManualBookingCreatedByActor(booking = {}, actor = {}) {
  const createdByUid = String(booking.createdByUid || booking.createdByUserUid || booking.createdBy || "").trim();
  const actorUid = String(actor.uid || "").trim();
  const source = norm(booking.bookingSource || booking.source || booking.origin);
  const createdFrom = norm(booking.createdFrom || booking.createdVia || booking.channel);
  const manual =
    booking.manual === true ||
    booking.isManual === true ||
    booking.adminCreated === true ||
    source.includes("manual") ||
    source.includes("admin") ||
    createdFrom.includes("admin") ||
    createdFrom.includes("calendar");

  return Boolean(manual && actorUid && createdByUid && actorUid === createdByUid);
}

function shouldShowToastForDevice({ businessId, booking, actor, deviceSettings }) {
  if (!deviceAllowsBookingToast(deviceSettings)) return false;
  if (!deviceAllowsBusinessToast(deviceSettings, businessId)) return false;

  const settings = mergeToastDeviceSettings(deviceSettings);
  if (settings.muteManualCreatedByMe !== false && isManualBookingCreatedByActor(booking, actor)) {
    return false;
  }

  return true;
}



function norm(value) {
  return String(value || "").trim().toLowerCase();
}

function getEffectiveUser(explicitUser = null) {
  try {
    const session = getCurrentEffectiveSession({ firebaseAuth: auth, userInfo: explicitUser || {} });
    if (isEffectiveSessionLoggedIn(session)) {
      return {
        uid: session.user?.uid ? String(session.user.uid) : "",
        email: norm(session.user?.email || explicitUser?.email || ""),
        displayName: session.user?.displayName || explicitUser?.displayName || explicitUser?.nombre || explicitUser?.name || "",
      };
    }
  } catch (_) {}

  return { uid: "", email: "", displayName: "" };
}

function toastStorageKey(actor = {}) {
  return makeUserScopedStorageKey(TOAST_STORAGE_KEY, actor || {});
}

function safeReadSeen(actor = {}) {
  try {
    const raw = localStorage.getItem(toastStorageKey(actor));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function safeWriteSeen(seen, actor = {}) {
  try {
    const entries = Object.entries(seen || {})
      .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
      .slice(0, 220);
    localStorage.setItem(toastStorageKey(actor), JSON.stringify(Object.fromEntries(entries)));
  } catch (_) {}
}

function markSeen(toastKey, actor = {}) {
  const seen = safeReadSeen(actor);
  seen[toastKey] = Date.now();
  safeWriteSeen(seen, actor);
}

function wasSeen(toastKey, actor = {}) {
  const seen = safeReadSeen(actor);
  return Boolean(seen && seen[toastKey]);
}

function timestampToMs(value) {
  try {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const t = new Date(value).getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (value.seconds) return Number(value.seconds) * 1000;
  } catch (_) {}
  return 0;
}

function bookingCreatedMs(booking) {
  return (
    timestampToMs(booking?.createdAt) ||
    timestampToMs(booking?.requestedAt) ||
    timestampToMs(booking?.submittedAt) ||
    timestampToMs(booking?.scheduledAt) ||
    Date.now()
  );
}

function isPendingBooking(booking) {
  const status = norm(booking?.status || booking?.confirmationStatus);
  return ["pending", "pendiente", "to_confirm", "por_confirmar"].includes(status);
}

function isOnlineBooking(booking) {
  const source = norm(booking?.bookingSource || booking?.source || booking?.origin);
  const createdFrom = norm(booking?.createdFrom || booking?.createdVia || booking?.channel);
  if (["client_online", "online", "public_booking", "customer_online"].includes(source)) return true;
  if (["booking_interface", "public_booking", "client_booking", "createbookingfunction"].includes(createdFrom)) return true;
  if (booking?.onlineBooking === true || booking?.createdByClient === true || booking?.requestedByClient === true) return true;
  if (booking?.adminCreated === true || booking?.manual === true) return false;
  if (createdFrom.includes("admin") || source.includes("manual")) return false;
  return false;
}

function canFastApproveBooking(booking) {
  if (!booking) return false;
  if (booking.employeeNotRequired === true || booking.noWorkerMode === true) return true;
  if (booking.requestedWorkerMode === "none") return true;
  if (booking.employeeId || booking.workerId || booking.resourceId) return true;
  if (Array.isArray(booking.assignedEmployeeIds) && booking.assignedEmployeeIds.length > 0) return true;
  if (Array.isArray(booking.assignedEmployees) && booking.assignedEmployees.length > 0) return true;
  return false;
}

function getClientName(booking) {
  return (
    booking?.customerName ||
    booking?.clientName ||
    booking?.name ||
    booking?.customer?.name ||
    "Cliente"
  );
}

function getServiceName(booking) {
  return (
    booking?.serviceName ||
    booking?.service ||
    booking?.packageName ||
    booking?.serviceTitle ||
    "Reserva"
  );
}

function getBookingTimeLabel(booking) {
  try {
    const date = booking?.scheduledAt || booking?.start || booking?.date || booking?.createdAt;
    const d = date && typeof date.toDate === "function" ? date.toDate() : new Date(date);
    if (d && !Number.isNaN(d.getTime())) {
      return d.toLocaleString("es-PA", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
    }
  } catch (_) {}

  const pieces = [booking?.date, booking?.time].filter(Boolean);
  return pieces.join(" · ");
}

function getBookingPriceLabel(booking) {
  const value = booking?.totalPrice ?? booking?.total ?? booking?.price ?? booking?.servicePrice;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function normalizeBusinessDoc(id, data = {}) {
  return {
    id,
    ...data,
    routeParam: getBusinessRouteParam({ id, ...data }),
  };
}

function dedupeBusinesses(list) {
  const seen = new Set();
  const out = [];
  for (const item of list || []) {
    if (!item?.id) continue;
    const key = String(item.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

async function loadManagedBusinesses({ firestore, actor }) {
  if (!firestore || (!actor.uid && !actor.email)) return [];
  const queries = [];

  try {
    if (actor.uid) {
      queries.push(query(collection(firestore, "businesses"), where("ownerUid", "==", actor.uid), limit(20)));
      queries.push(query(collection(firestore, "businesses"), where("requestedByUid", "==", actor.uid), limit(20)));
      queries.push(query(collection(firestore, "businesses"), where(`admins.${actor.uid}`, "==", true), limit(20)));
      queries.push(query(collection(firestore, "businesses"), where(`managers.${actor.uid}`, "==", true), limit(20)));
    }
    if (actor.email) {
      queries.push(query(collection(firestore, "businesses"), where("ownerEmail", "==", actor.email), limit(20)));
      queries.push(query(collection(firestore, "businesses"), where("requestedByEmail", "==", actor.email), limit(20)));
    }
  } catch (_) {}

  const loaded = [];
  const results = await Promise.allSettled(queries.map((q) => getDocs(q)));
  results.forEach((res) => {
    if (res.status !== "fulfilled") return;
    res.value.forEach((snap) => {
      loaded.push(normalizeBusinessDoc(snap.id, snap.data() || {}));
    });
  });

  return dedupeBusinesses(loaded).slice(0, 30);
}

async function loadBusinessNotificationSettings({ firestore, business }) {
  const fallback = business?.notificationSettings && typeof business.notificationSettings === "object"
    ? business.notificationSettings
    : {};

  try {
    const snap = await getDoc(doc(firestore, "businesses", business.id, "notificationSettings", "main"));
    if (snap.exists()) {
      return { ...fallback, ...(snap.data() || {}) };
    }
  } catch (_) {}

  return fallback;
}

function shouldShowForBusiness(settings) {
  if (!settings || typeof settings !== "object") return true;
  if (settings.popupNewBookingInApp === false) return false;
  if (settings.notifyOwnerOnNewBooking === false && settings.notifyAdminsOnNewBooking === false) return false;
  return true;
}

function buildToastKey(businessId, bookingId) {
  return `${businessId}:${bookingId}`;
}

function BookingNotificationToasts({ user = null, db = defaultDb }) {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const [deviceSettings, setDeviceSettings] = useState(() => mergeToastDeviceSettings({ loaded: false }));
  const unsubRef = useRef([]);
  const mountedRef = useRef(false);
  const deviceId = useMemo(() => getStoredNotificationDeviceId(), []);

  const actor = useMemo(() => getEffectiveUser(user), [user?.uid, user?.email]);

  const dismissToast = useCallback((toastKey) => {
    markSeen(toastKey, actor);
    setToasts((prev) => prev.filter((item) => item.toastKey !== toastKey));
  }, [actor]);

  const pushToast = useCallback((item) => {
    if (!item?.toastKey || wasSeen(item.toastKey, actor)) return;
    markSeen(item.toastKey, actor);
    setToasts((prev) => {
      if (prev.some((x) => x.toastKey === item.toastKey)) return prev;
      return [item, ...prev].slice(0, MAX_TOASTS);
    });
  }, [actor]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!db || !actor.uid || !deviceId) {
      setDeviceSettings(mergeToastDeviceSettings({ loaded: true }));
      return undefined;
    }

    const ref = doc(db, "users", actor.uid, "notificationDevices", deviceId);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setDeviceSettings(mergeToastDeviceSettings({ ...(snap.data() || {}), loaded: true }));
      } else {
        setDeviceSettings(mergeToastDeviceSettings({ loaded: true }));
      }
    }, (err) => {
      try { console.warn("[BookingNotificationToasts] device settings listen failed", err); } catch (_) {}
      setDeviceSettings(mergeToastDeviceSettings({ loaded: true }));
    });

    return () => {
      try { unsub(); } catch (_) {}
    };
  }, [db, actor.uid, deviceId]);


  useEffect(() => {
    unsubRef.current.forEach((fn) => {
      try { fn(); } catch (_) {}
    });
    unsubRef.current = [];
    setToasts([]);

    if (!db || (!actor.uid && !actor.email)) return undefined;

    let cancelled = false;

    async function start() {
      const businesses = await loadManagedBusinesses({ firestore: db, actor });
      const allowedBusinesses = (businesses || []).filter((business) => deviceAllowsBusinessToast(deviceSettings, business.id));
      if (cancelled || !mountedRef.current || !allowedBusinesses.length) return;

      for (const business of allowedBusinesses) {
        const settings = await loadBusinessNotificationSettings({ firestore: db, business });
        if (cancelled || !mountedRef.current) return;
        if (!shouldShowForBusiness(settings)) continue;

        let firstSnapshot = true;
        const knownThisListener = new Set();

        try {
          const bookingsQuery = query(
            collection(db, "businesses", business.id, "bookings"),
            where("status", "==", "pending"),
            limit(25)
          );

          const unsub = onSnapshot(bookingsQuery, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
              if (change.type !== "added" && change.type !== "modified") return;
              const booking = { id: change.doc.id, ...(change.doc.data() || {}) };
              const toastKey = buildToastKey(business.id, booking.id);

              if (knownThisListener.has(toastKey)) return;
              knownThisListener.add(toastKey);

              if (!shouldShowToastForDevice({ businessId: business.id, booking, actor, deviceSettings })) {
                markSeen(toastKey, actor);
                return;
              }

              if (!isPendingBooking(booking) || !isOnlineBooking(booking)) {
                markSeen(toastKey, actor);
                return;
              }

              const createdMs = bookingCreatedMs(booking);
              const isRecentInitial = firstSnapshot && Date.now() - createdMs <= INITIAL_RECENT_MS;

              if (firstSnapshot && !isRecentInitial) {
                markSeen(toastKey, actor);
                return;
              }

              pushToast({
                toastKey,
                business,
                booking,
                createdMs,
              });
            });

            firstSnapshot = false;
          }, (err) => {
            try { console.warn("[BookingNotificationToasts] listener failed", business.id, err); } catch (_) {}
          });

          unsubRef.current.push(unsub);
        } catch (err) {
          try { console.warn("[BookingNotificationToasts] could not subscribe", business.id, err); } catch (_) {}
        }
      }
    }

    start();

    return () => {
      cancelled = true;
      unsubRef.current.forEach((fn) => {
        try { fn(); } catch (_) {}
      });
      unsubRef.current = [];
    };
  }, [db, actor.uid, actor.email, pushToast, deviceSettings.scopeMode, JSON.stringify(deviceSettings.businessScopes || []), JSON.stringify(deviceSettings.notificationTypes || {}), deviceSettings.muteManualCreatedByMe]);

  const openBusinessAgenda = useCallback((business, booking) => {
    try {
      const path = businessAdminPath(business, "calendar");
      navigate(`${path}?panel=pending&bookingId=${encodeURIComponent(booking?.id || "")}`);
      return;
    } catch (_) {}

    try {
      navigate(`/business/${encodeURIComponent(business?.routeParam || business?.id || "")}/calendar?panel=pending&bookingId=${encodeURIComponent(booking?.id || "")}`);
    } catch (_) {}
  }, [navigate]);

  const approveBooking = useCallback(async (item) => {
    const { business, booking, toastKey } = item || {};
    if (!business?.id || !booking?.id) return;

    if (!canFastApproveBooking(booking)) {
      openBusinessAgenda(business, booking);
      return;
    }

    try {
      if (isApprovalFirstEvent(booking)) {
        await approveEventBooking(business.id, booking.id);
        dismissToast(toastKey);
        return;
      }
      await updateDoc(doc(db, "businesses", business.id, "bookings", booking.id), {
        status: "confirmed",
        confirmationStatus: "confirmed",
        approvedAt: serverTimestamp(),
        acceptedAt: serverTimestamp(),
        approvedBy: actor.uid || null,
        approvedByEmail: actor.email || null,
        updatedAt: serverTimestamp(),
      });
      dismissToast(toastKey);
    } catch (err) {
      console.error("[BookingNotificationToasts] approve failed", err);
      openBusinessAgenda(business, booking);
    }
  }, [actor.email, actor.uid, db, dismissToast, openBusinessAgenda]);

  if (!toasts.length) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="loop-booking-toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((item) => {
          const booking = item.booking || {};
          const business = item.business || {};
          const canApprove = canFastApproveBooking(booking);
          const price = getBookingPriceLabel(booking);

          return (
            <article className="loop-booking-toast" key={item.toastKey}>
              <button
                type="button"
                className="loop-booking-toast-close"
                onClick={() => dismissToast(item.toastKey)}
                aria-label="Cerrar notificación"
              >
                ×
              </button>

              <div className="loop-booking-toast-kicker">Nueva reserva online</div>
              <h3>{getClientName(booking)}</h3>
              <p>{getServiceName(booking)}</p>

              <div className="loop-booking-toast-meta">
                <span>{business.name || "Negocio"}</span>
                {getBookingTimeLabel(booking) ? <span>{getBookingTimeLabel(booking)}</span> : null}
                {price ? <span>{price}</span> : null}
              </div>

              {!canApprove ? (
                <div className="loop-booking-toast-hint">
                  Requiere revisar/asignar trabajador antes de aprobar.
                </div>
              ) : null}

              <div className="loop-booking-toast-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => openBusinessAgenda(business, booking)}
                >
                  Ver agenda
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => canApprove ? approveBooking(item) : openBusinessAgenda(business, booking)}
                >
                  {canApprove ? "Aprobar" : "Asignar"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}

const styles = `
  .loop-booking-toast-stack {
    position: fixed;
    right: max(18px, env(safe-area-inset-right, 0px));
    bottom: max(18px, env(safe-area-inset-bottom, 0px));
    z-index: 2147482500;
    display: grid;
    gap: 12px;
    width: min(390px, calc(100vw - 28px));
    pointer-events: none;
  }

  .loop-booking-toast {
    position: relative;
    pointer-events: auto;
    border-radius: 28px;
    border: 1px solid rgba(226, 232, 240, 0.92);
    background:
      radial-gradient(circle at 0% 0%, rgba(20, 184, 166, 0.14), transparent 34%),
      rgba(255, 255, 255, 0.96);
    box-shadow: 0 26px 70px rgba(15, 23, 42, 0.22);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    padding: 18px;
    color: #0f172a;
    animation: loopBookingToastIn 220ms ease-out both;
  }

  @keyframes loopBookingToastIn {
    from { opacity: 0; transform: translateY(14px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  .loop-booking-toast-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 30px;
    height: 30px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #64748b;
    font-size: 20px;
    line-height: 1;
    font-weight: 800;
    cursor: pointer;
  }

  .loop-booking-toast-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    padding: 7px 11px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #047857;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .loop-booking-toast-kicker::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #10b981;
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.13);
  }

  .loop-booking-toast h3 {
    margin: 14px 38px 0 0;
    font-size: 21px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.04em;
    color: #0f172a;
  }

  .loop-booking-toast p {
    margin: 5px 0 0;
    color: #475569;
    font-size: 14px;
    line-height: 1.35;
    font-weight: 760;
  }

  .loop-booking-toast-meta {
    margin-top: 13px;
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }

  .loop-booking-toast-meta span {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 850;
  }

  .loop-booking-toast-hint {
    margin-top: 12px;
    border-radius: 16px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    color: #9a3412;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 800;
  }

  .loop-booking-toast-actions {
    display: flex;
    gap: 9px;
    margin-top: 15px;
  }

  .loop-booking-toast-actions button {
    flex: 1 1 0;
    min-height: 44px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 950;
    border: 1px solid transparent;
  }

  .loop-booking-toast-actions .secondary {
    background: #ffffff;
    color: #0f172a;
    border-color: #e2e8f0;
  }

  .loop-booking-toast-actions .primary {
    background: linear-gradient(135deg, #00675f 0%, #00897f 100%);
    color: #ffffff;
    box-shadow: 0 12px 26px rgba(0, 103, 95, 0.18);
  }

  @media (max-width: 640px) {
    .loop-booking-toast-stack {
      right: 10px;
      left: 10px;
      bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      width: auto;
    }

    .loop-booking-toast {
      border-radius: 24px;
      padding: 16px;
    }

    .loop-booking-toast h3 {
      font-size: 19px;
    }
  }
`;

export default BookingNotificationToasts;
